/**
 * Attendance self-service API client. Wraps the employee /v1/me/* attendance
 * endpoints (ADR-0014 Phase 4 PR-2). All self-scoped — the employee is derived
 * from the auth token server-side; there is no employeeId param.
 */
import { apiRequest } from './client.js';
import type { OvertimeItem } from './overtime.js';

export type ClockDirection = 'clock_in' | 'clock_out';

export interface ClockEvent {
  id: string;
  employeeId: string;
  occurredAt: string;
  direction: ClockDirection;
  source: string;
  geofenceOutOfRange: boolean;
  note: string | null;
}

export interface TimesheetPeriod {
  id: string;
  employeeId: string;
  periodStart: string;
  periodEnd: string;
  operatingMode: string;
  regularHours: string;
  overtimeHours: string;
  absenceDays: string;
  standardHoursApplied: string;
}

/** Self clock punch (POST /v1/me/clock). Optional GPS (D-8 amendment / Phase 6):
 *  the server computes geofenceOutOfRange from the employee's assigned site
 *  (flag-not-block). No coordinates ⇒ never flagged. */
export function selfClock(body: { direction: ClockDirection; occurredAt?: string; latitude?: number; longitude?: number }): Promise<ClockEvent> {
  return apiRequest<ClockEvent>('/v1/me/clock', { method: 'POST', body });
}

export function listMyClockEvents(): Promise<{ events: ClockEvent[] }> {
  return apiRequest<{ events: ClockEvent[] }>('/v1/me/clock-events');
}

/** The employee's assigned geofence work site (transparency). `location` is
 *  null when they're not geofenced (clock in from anywhere). */
export interface MyWorkLocation {
  location: { name: string; radiusMeters: number } | null;
}
export function getMyWorkLocation(): Promise<MyWorkLocation> {
  return apiRequest<MyWorkLocation>('/v1/me/work-location');
}

export function listMyTimesheetPeriods(): Promise<{ periods: TimesheetPeriod[] }> {
  return apiRequest<{ periods: TimesheetPeriod[] }>('/v1/me/timesheet-periods');
}

export function listMyOvertime(): Promise<{ items: OvertimeItem[] }> {
  return apiRequest<{ items: OvertimeItem[] }>('/v1/me/overtime');
}

// ---------------------------------------------------------------------------
// HR / admin surfaces (Phase 4 PR-3). HR-gated on the API.
// ---------------------------------------------------------------------------

/** GET /v1/attendance/timesheet-periods — all periods (HR), optional filters. */
export function listTimesheetPeriods(
  q: { employeeId?: string; periodStart?: string; periodEnd?: string } = {},
): Promise<{ periods: TimesheetPeriod[] }> {
  const params = new URLSearchParams();
  if (q.employeeId) params.set('employeeId', q.employeeId);
  if (q.periodStart) params.set('periodStart', q.periodStart);
  if (q.periodEnd) params.set('periodEnd', q.periodEnd);
  const qs = params.toString();
  return apiRequest<{ periods: TimesheetPeriod[] }>(`/v1/attendance/timesheet-periods${qs ? `?${qs}` : ''}`);
}

export interface AggregateWarning {
  employeeId: string;
  date: string;
  reason: string;
}

/** POST /v1/attendance/timesheet-periods — (re)aggregate a period (HR). */
export function aggregateTimesheetPeriods(
  body: { periodStart: string; periodEnd: string; employeeId?: string },
): Promise<{ periods: TimesheetPeriod[]; warnings: AggregateWarning[] }> {
  return apiRequest('/v1/attendance/timesheet-periods', { method: 'POST', body });
}

export interface PunchLogResult {
  committed: boolean;
  /** Dry-run only. */
  wouldImport?: number;
  /** Commit only. */
  imported?: number;
  skippedDuplicates?: number;
  unmapped: Array<{ row: number; deviceId: string; externalUserId: string }>;
  rowErrors: Array<{ row: number; message: string }>;
}

/** POST /v1/attendance/imports — punch-log CSV. commit=false dry-runs; the same
 *  file is re-posted with commit=true to commit (the importer holds no server
 *  state between calls). */
export function importPunchLog(file: File, commit: boolean): Promise<PunchLogResult> {
  const form = new FormData();
  form.append('commit', String(commit));
  form.append('file', file);
  return apiRequest<PunchLogResult>('/v1/attendance/imports', { method: 'POST', body: form });
}

/** POST /v1/attendance/clock — HR records a punch for an employee. */
export function manualClock(body: {
  employeeId: string;
  occurredAt: string;
  direction: ClockDirection;
  note?: string;
}): Promise<ClockEvent> {
  return apiRequest<ClockEvent>('/v1/attendance/clock', { method: 'POST', body });
}

// ---------------------------------------------------------------------------
// Config (PR-4): work patterns + device mappings + clock-event review.
// ---------------------------------------------------------------------------

export type AttendanceOperatingMode = 'salaried_exception' | 'hourly';

export interface WorkPattern {
  id: string;
  employeeId: string;
  operatingMode: AttendanceOperatingMode;
  standardHoursPerWeek: string;
  overtimeMultiplier: string;
  /** Geofence site assignment (Phase 6). null ⇒ not geofenced. */
  locationId: string | null;
}

export function listWorkPatterns(): Promise<{ patterns: WorkPattern[] }> {
  return apiRequest<{ patterns: WorkPattern[] }>('/v1/attendance/work-patterns');
}

/** PUT /v1/attendance/work-patterns/:employeeId — upsert (1:1 with employee). */
export function upsertWorkPattern(
  employeeId: string,
  body: { operatingMode: AttendanceOperatingMode; standardHoursPerWeek: number; overtimeMultiplier: number; locationId?: string | null },
): Promise<WorkPattern> {
  return apiRequest<WorkPattern>(`/v1/attendance/work-patterns/${encodeURIComponent(employeeId)}`, { method: 'PUT', body });
}

// --- Geofence locations (Phase 6) -----------------------------------------

export interface AttendanceLocation {
  id: string;
  name: string;
  latitude: string;
  longitude: string;
  radiusMeters: number;
  active: boolean;
}

export function listLocations(): Promise<{ locations: AttendanceLocation[] }> {
  return apiRequest<{ locations: AttendanceLocation[] }>('/v1/attendance/locations');
}

export function createLocation(body: { name: string; latitude: number; longitude: number; radiusMeters: number }): Promise<AttendanceLocation> {
  return apiRequest<AttendanceLocation>('/v1/attendance/locations', { method: 'POST', body });
}

export function deleteLocation(id: string): Promise<void> {
  return apiRequest<void>(`/v1/attendance/locations/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function deleteWorkPattern(employeeId: string): Promise<void> {
  return apiRequest<void>(`/v1/attendance/work-patterns/${encodeURIComponent(employeeId)}`, { method: 'DELETE' });
}

export interface DeviceMapping {
  id: string;
  employeeId: string;
  deviceId: string;
  externalUserId: string;
}

export function listDeviceMappings(): Promise<{ mappings: DeviceMapping[] }> {
  return apiRequest<{ mappings: DeviceMapping[] }>('/v1/attendance/device-mappings');
}

export function createDeviceMapping(body: { deviceId: string; externalUserId: string; employeeId: string }): Promise<DeviceMapping> {
  return apiRequest<DeviceMapping>('/v1/attendance/device-mappings', { method: 'POST', body });
}

export function deleteDeviceMapping(id: string): Promise<void> {
  return apiRequest<void>(`/v1/attendance/device-mappings/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** GET /v1/attendance/clock-events — raw punch ledger (HR review). */
export function listClockEvents(q: { employeeId?: string; from?: string; to?: string; outOfRangeOnly?: boolean } = {}): Promise<{ events: ClockEvent[] }> {
  const params = new URLSearchParams();
  if (q.employeeId) params.set('employeeId', q.employeeId);
  if (q.from) params.set('from', q.from);
  if (q.to) params.set('to', q.to);
  if (q.outOfRangeOnly) params.set('outOfRangeOnly', 'true');
  const qs = params.toString();
  return apiRequest<{ events: ClockEvent[] }>(`/v1/attendance/clock-events${qs ? `?${qs}` : ''}`);
}
